import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Mail, MapPin, Phone } from "lucide-react";
import type { ReactNode } from "react";
import { ASSETS } from "#/lib/assets";
import { PillHero } from "../components/PillHero.js";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({ meta: [{ title: "Privacy Policy" }] }),
  component: PrivacyPolicyPage,
});

const SUPPORT_EMAIL = "support@learningplus.ph";
const SUPPORT_PHONE_LABEL = "+63 2 8540 9868";
const SUPPORT_PHONE_HREF = "tel:+63285409868";
const ADDRESS = "339 Robinson Circle, Pasig City 1600, Philippines";

const BODY = "font-display text-base text-lp-text-dk leading-relaxed";
const LINK = "font-semibold text-lp-green-dk underline underline-offset-2";

function EmailLink() {
  return (
    <a href={`mailto:${SUPPORT_EMAIL}`} className={LINK}>
      {SUPPORT_EMAIL}
    </a>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="m-0">{children}</p>;
}

function H3({ children }: { children: ReactNode }) {
  return (
    <h3 className="m-0 pt-2 font-display font-semibold text-lg text-lp-green-dk">
      {children}
    </h3>
  );
}

function BulletList({ children }: { children: ReactNode }) {
  return (
    <ul className="m-0 list-disc space-y-2 pl-6 marker:text-lp-green-mid">
      {children}
    </ul>
  );
}

function PlainItems({ items }: { items: ReadonlyArray<string> }) {
  return (
    <BulletList>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </BulletList>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return <b className="font-semibold text-lp-green-dk">{children}</b>;
}

function Lead({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li>
      <Strong>{label}</Strong> {children}
    </li>
  );
}

interface PolicyTableProps {
  headers: ReadonlyArray<string>;
  rows: ReadonlyArray<ReadonlyArray<string>>;
}

function PolicyTable({ headers, rows }: PolicyTableProps) {
  return (
    <>
      <div className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => (
          <dl
            key={row[0]}
            className="m-0 overflow-hidden rounded-2xl border border-lp-green-mid/40"
          >
            {row.map((cell, c) => (
              <div
                key={headers[c]}
                className={
                  c === 0
                    ? "bg-lp-green-soft px-4 py-3"
                    : "border-lp-green-mid/30 border-t px-4 py-3"
                }
              >
                <dt className="font-semibold text-lp-green-dk text-xs uppercase tracking-[0.12em]">
                  {headers[c]}
                </dt>
                <dd
                  className={
                    c === 0
                      ? "m-0 mt-1 font-semibold text-lp-green-dk"
                      : "m-0 mt-1"
                  }
                >
                  {cell}
                </dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
      <div className="hidden overflow-hidden rounded-2xl border border-lp-green-mid/40 md:block">
        <table className="w-full border-collapse text-left">
          <thead className="bg-lp-green-soft">
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="px-5 py-3 align-bottom font-semibold text-lp-green-dk text-sm"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[0]} className="border-lp-green-mid/30 border-t">
                {row.map((cell, c) => (
                  <td
                    key={headers[c]}
                    className={
                      c === 0
                        ? "px-5 py-3 align-top font-semibold text-lp-green-dk text-sm"
                        : "px-5 py-3 align-top text-sm"
                    }
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-3xl bg-lp-green-soft px-6 py-6 md:px-8 md:py-7">
      {children}
    </div>
  );
}

function ContactLines() {
  return (
    <ul className="m-0 mt-4 list-none space-y-3 p-0">
      <li className="flex items-start gap-3">
        <MapPin
          aria-hidden="true"
          size={20}
          className="mt-0.5 shrink-0 text-lp-green-dk"
        />
        <span>{ADDRESS}</span>
      </li>
      <li className="flex items-start gap-3">
        <Phone
          aria-hidden="true"
          size={20}
          className="mt-0.5 shrink-0 text-lp-green-dk"
        />
        <span>
          <span className="sr-only">Phone: </span>
          <a href={SUPPORT_PHONE_HREF} className={LINK}>
            {SUPPORT_PHONE_LABEL}
          </a>
        </span>
      </li>
      <li className="flex items-start gap-3">
        <Mail
          aria-hidden="true"
          size={20}
          className="mt-0.5 shrink-0 text-lp-green-dk"
        />
        <span>
          <span className="sr-only">Email: </span>
          <EmailLink />
        </span>
      </li>
    </ul>
  );
}

interface PolicySection {
  id: string;
  title: string;
  body: ReactNode;
}

const SECTIONS: ReadonlyArray<PolicySection> = [
  {
    id: "what-we-collect",
    title: "1. What we collect",
    body: (
      <>
        <H3>Information you give us</H3>
        <BulletList>
          <Lead label="When you create an account:">
            your name, email address, phone number and a password. We never see
            your actual password because we only keep a scrambled (hashed)
            version of it.
          </Lead>
          <Lead label="When you update your profile:">
            your name, phone number, and optionally a company name. If you
            change your email address, we send a link to the new address to
            confirm it.
          </Lead>
          <Lead label="When you save addresses:">
            the name, phone number and full address (street, barangay, city,
            province, postal code and country).
          </Lead>
          <li>
            <Strong>When you place an order</Strong>, with or without an
            account: your name, email, phone, shipping and billing addresses,
            what you ordered, the shipping method, any discount code, and for
            store pickup, the hub and time slot you chose.
          </li>
          <Lead label="When you pay:">
            which payment method you used and its reference number. See
            "Payments" below.
          </Lead>
          <Lead label="When you track an order as a guest:">
            the email address and order number you type in.
          </Lead>
          <Lead label="When you unlock a private catalog:">
            the access password you enter.
          </Lead>
          <Lead label="When you use our Contact page:">
            your name, email, phone, the type of concern, your message, any
            extra email addresses you copy in, and any file you attach.
          </Lead>
          <Lead label="When you chat with us:">
            your chat messages and anything you choose to share, such as your
            name or email.
          </Lead>
        </BulletList>
        <H3>Information we collect automatically</H3>
        <BulletList>
          <Lead label="Shopping activity:">
            what is in your cart (including carts you leave without checking
            out) and your order history.
          </Lead>
          <Lead label="Where you came from:">
            if you click a link from one of our campaigns, your browser
            remembers which campaign it was for 30 days, and we attach that to
            your order. This tells us which campaigns work. It does not identify
            you by itself.
          </Lead>
          <Lead label="Technical information:">
            like every website, our servers and our network provider see your IP
            address, browser type and the pages you visit. We need this to
            deliver the site and protect it from attacks.
          </Lead>
          <Lead label="Live chat:">
            our chat service sees which of our pages you visit and basic
            information about your device, so our support team has context when
            you start a chat.
          </Lead>
        </BulletList>
        <P>
          We do <Strong>not</Strong> use advertising trackers.
        </P>
        <H3>Payments</H3>
        <P>
          Payments are handled by licensed payment processors (PayMongo and
          Dragonpay). When you pay by card, your card details go straight from
          your browser to the payment processor.{" "}
          <Strong>Your card number never reaches our servers.</Strong> We only
          keep whether the payment went through, the amount, the method and the
          reference numbers.
        </P>
        <H3>What we don't collect</H3>
        <P>
          We never ask for sensitive information such as government ID numbers,
          health information, or religious or political views. Please don't
          include these in messages or chats.
        </P>
      </>
    ),
  },
  {
    id: "why-we-use-it",
    title: "2. Why we use your information",
    body: (
      <>
        <P>
          The law requires us to have a valid reason for each use. Here is ours:
        </P>
        <PolicyTable
          headers={["What we do", "Why we're allowed to"]}
          rows={[
            [
              "Run your account and sign you in",
              "We need it to provide the service you signed up for",
            ],
            [
              "Process, pack, ship or hand over your order",
              "We need it to fulfil your order",
            ],
            [
              "Take payment and handle refunds",
              "We need it to fulfil your order",
            ],
            [
              "Send emails about your account and orders (verification, password reset, order and payment confirmations)",
              "We need it to fulfil your order",
            ],
            [
              "Answer your questions and support requests",
              "To serve you as a customer",
            ],
            [
              "Keep sales and accounting records",
              "Tax and accounting laws require it",
            ],
            [
              "Keep a customer record with your purchase history, and follow up on carts you didn't finish",
              "Our legitimate interest in running and improving the store",
            ],
            [
              "See which campaigns bring customers to us",
              "Our legitimate interest",
            ],
            ["Apply discount codes", "We need it to fulfil your order"],
            ["Protect the site and prevent fraud", "Our legitimate interest"],
          ]}
        />
        <P>
          We do <Strong>not</Strong> sell your personal information, and we
          don't use it to make automated decisions about you.
        </P>
      </>
    ),
  },
  {
    id: "who-we-share-with",
    title: "3. Who we share it with",
    body: (
      <>
        <P>
          We work with a small number of trusted service providers to run the
          store. Each one only gets the information it needs to do its job for
          us, and may only use it for that purpose:
        </P>
        <PolicyTable
          headers={["Type of provider", "What they get", "Why"]}
          rows={[
            [
              "Payment processors (PayMongo and Dragonpay)",
              "Payment details, billing name, email, phone, billing address, amount",
              "Processing your payment",
            ],
            [
              "Order and inventory management",
              "Name, email, phone, addresses, what you ordered",
              "Managing stock, packing and shipping orders",
            ],
            [
              "Customer relationship management",
              "Name, email, phone, purchase history, unfinished carts, discount codes, campaign source",
              "Keeping customer records and following up on unfinished carts",
            ],
            [
              "Customer support and live chat",
              "Your messages, attachments, and the pages you visit while chatting",
              "Answering your questions",
            ],
            [
              "Email delivery",
              "Your email, name and the order or account details in the email",
              "Sending account and order emails",
            ],
            [
              "Couriers and our pickup hubs",
              "Recipient name, phone, delivery address, package details, or for pickup, your order number and time slot",
              "Delivering your order or handing it over",
            ],
            [
              "Shipment tracking",
              "Tracking numbers only",
              "Showing delivery status on our Track Your Order page",
            ],
            [
              "Hosting and security",
              "Information stored in our systems and website traffic, including IP addresses",
              "Running the website securely and protecting it from attacks",
            ],
          ]}
        />
        <P>
          We may also share information when the law requires it, for example a
          court order or a lawful request from a government agency, or to defend
          a legal claim. You can ask us for the names of the providers that
          handle your information by emailing us (see{" "}
          <a href="#contact-us" className={LINK}>
            section 10
          </a>
          ).
        </P>
      </>
    ),
  },
  {
    id: "outside-the-philippines",
    title: "4. Data stored outside the Philippines",
    body: (
      <P>
        Some of our service providers store or process data outside the
        Philippines. We remain responsible for your information wherever it
        goes, and we only use providers that protect it to a standard comparable
        to Philippine law.
      </P>
    ),
  },
  {
    id: "how-long-we-keep-it",
    title: "5. How long we keep it",
    body: (
      <>
        <PolicyTable
          headers={["Information", "How long"]}
          rows={[
            [
              "Your account and profile",
              "As long as your account is open. If you ask us to delete it, we delete or anonymize it, except for the order records below.",
            ],
            [
              "Orders, invoices and payments",
              "As long as Philippine tax and accounting laws require",
            ],
            [
              "Customer records in our CRM",
              "Up to 5 years after your last purchase or contact, unless you ask us to delete them sooner",
            ],
            [
              "Unfinished carts",
              "Cleared when you come back and check out; otherwise kept with your customer record",
            ],
            [
              "Support tickets and chats",
              "Up to 3 years after the issue is closed",
            ],
            [
              "Campaign source in your browser",
              "30 days, then deleted automatically",
            ],
            ["Server and security logs", "Up to 90 days"],
          ]}
        />
        <P>
          When we no longer need information, we securely delete or anonymize
          it.
        </P>
      </>
    ),
  },
  {
    id: "how-we-protect-it",
    title: "6. How we protect it",
    body: (
      <>
        <PlainItems
          items={[
            "All connections to our site are encrypted (HTTPS).",
            "Passwords are stored only in scrambled form.",
            "Card details go straight to our payment processor and never touch our systems.",
            "Email verification and password reset links expire.",
            "Only staff who need customer data for their work can access it.",
          ]}
        />
        <P>
          No system is 100% secure. If a data breach happens that the law
          requires us to report, we will notify the National Privacy Commission
          and the people affected within the required time.
        </P>
      </>
    ),
  },
  {
    id: "cookies",
    title: "7. Cookies and browser storage",
    body: (
      <>
        <P>
          Our own site does <Strong>not</Strong> use advertising or analytics
          cookies. It saves a few small items in your browser so the store
          works:
        </P>
        <PlainItems
          items={[
            "keeping you signed in",
            "remembering your cart and wishlist",
            "remembering your shipping region and currency",
            "remembering private catalogs you have unlocked",
            "remembering which campaign brought you here (30 days)",
          ]}
        />
        <P>
          Our live chat and website security services may also set cookies, to
          keep chat conversations going and to tell real visitors from bots.
        </P>
        <P>
          You can clear or block these in your browser settings. If you do,
          you'll be signed out and your cart may be emptied.
        </P>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "8. Your rights",
    body: (
      <>
        <P>Under the Data Privacy Act, you have the right to:</P>
        <BulletList>
          <Lead label="Be informed">
            about how we collect and use your information. This policy does
            that.
          </Lead>
          <Lead label="See">the information we have about you.</Lead>
          <Lead label="Object">
            to how we use it, including follow-ups on unfinished carts, and take
            back any consent you gave.
          </Lead>
          <Lead label="Correct">
            anything that's wrong. You can update most details yourself on your
            account page.
          </Lead>
          <Lead label="Have it deleted or blocked">
            if it's wrong, outdated, unlawfully obtained or no longer needed.
            Records the law requires us to keep, such as invoices, can only be
            deleted after the required period.
          </Lead>
          <Lead label="Get a copy">
            of your information in a common electronic format.
          </Lead>
          <Lead label="Be compensated">
            for damage caused by wrong or unauthorized use of your information.
          </Lead>
          <Lead label="Complain">
            to the National Privacy Commission at{" "}
            <a
              href="https://www.privacy.gov.ph"
              target="_blank"
              rel="noopener noreferrer"
              className={LINK}
            >
              www.privacy.gov.ph
            </a>
            .
          </Lead>
        </BulletList>
        <Callout>
          <P>
            <Strong>To make a request,</Strong> email <EmailLink /> with the
            subject "Data Privacy Request". Tell us what you'd like us to do and
            give the email address on your account or order. We may need to
            confirm your identity first. We'll reply within 15 working days.
            Your lawful heirs or representatives can also make requests on your
            behalf.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "children",
    title: "9. Children",
    body: (
      <P>
        We serve homeschooling families, but our customers are the parents,
        guardians and teachers who buy materials for their students. Our site is
        meant for adults, and we don't knowingly create accounts for or collect
        information directly from anyone under 18. Some order details, such as a
        book's grade level, may relate to a child, but they come from the adult
        placing the order. If you think a child has given us information without
        a parent's or guardian's permission, email <EmailLink /> and we'll
        delete it.
      </P>
    ),
  },
  {
    id: "contact-us",
    title: "10. Contact us",
    body: (
      <>
        <P>
          For questions about this policy or your information, contact our Data
          Protection Officer:
        </P>
        <Callout>
          <p className="m-0 font-display font-semibold text-lg text-lp-green-dk">
            Data Protection Officer, Learning Plus
          </p>
          <ContactLines />
        </Callout>
      </>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to this policy",
    body: (
      <P>
        We may update this policy when our services, our providers or the law
        change. We'll post the new version here with a new effective date. If a
        change significantly affects how we use your information, we'll also put
        a notice on the site or email registered customers before it takes
        effect.
      </P>
    ),
  },
];

function TocLinks() {
  return (
    <ol className="m-0 list-none space-y-1 p-0">
      {SECTIONS.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            className="block rounded-lg px-3 py-1.5 font-display text-lp-text-dk text-sm leading-snug no-underline transition-colors hover:bg-lp-green-soft hover:text-lp-green-dk"
          >
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  );
}

function PrivacyPolicyPage() {
  return (
    <div data-screen-label="Privacy Policy" className="pb-24">
      <PillHero
        content={
          <div>
            <h1 className="m-0 whitespace-pre-line font-bold font-display text-5xl text-lp-green-dk leading-[1.05] md:text-6xl">
              {"PRIVACY\nPOLICY"}
            </h1>
            <p className="m-0 mt-4 font-display font-medium text-base text-lp-green-mid">
              Effective 8 October 2026
            </p>
          </div>
        }
        asset={
          <img
            src={ASSETS.heroPrivacy}
            alt=""
            className="h-56 w-auto object-cover md:h-96 md:-mb-20 md:-ml-12"
          />
        }
        assetAlign="edge"
        assetClassName="md:-my-28 md:-ml-24"
      />

      <div className="mx-auto mt-8 w-full max-w-figma px-6 md:mt-12 md:px-10">
        <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16">
          <aside className="mb-10 lg:mb-0">
            <details className="group rounded-2xl border border-lp-green-mid/40 lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 font-display font-semibold text-lp-green-dk [&::-webkit-details-marker]:hidden">
                On this page
                <ChevronDown
                  aria-hidden="true"
                  size={20}
                  className="shrink-0 text-lp-green-mid transition-transform group-open:rotate-180"
                />
              </summary>
              <nav aria-label="Privacy policy sections" className="px-2 pb-4">
                <TocLinks />
              </nav>
            </details>
            <nav
              aria-label="Privacy policy sections"
              className="sticky top-28 hidden lg:block"
            >
              <p className="m-0 mb-3 px-3 font-display font-semibold text-lp-green-dk text-xs uppercase tracking-[0.18em]">
                On this page
              </p>
              <TocLinks />
            </nav>
          </aside>

          <article className={`max-w-[70ch] ${BODY}`}>
            <div className="flex flex-col gap-4">
              <P>
                Learning Plus runs the online bookstore at learningplus.ph,
                where we sell homeschool curriculum, books and learning
                materials. This policy explains, in plain language, what
                personal information we collect, why we need it, who we share it
                with, and what you can do about it.
              </P>
              <P>
                We follow the Data Privacy Act of 2012 (Republic Act No. 10173),
                its implementing rules, and the guidelines of the National
                Privacy Commission.
              </P>
            </div>

            <div className="mt-8">
              <Callout>
                <p className="m-0 font-display font-semibold text-lg text-lp-green-dk">
                  Who we are: Learning Plus
                </p>
                <ContactLines />
              </Callout>
            </div>

            {SECTIONS.map((s) => (
              <section
                key={s.id}
                id={s.id}
                aria-labelledby={`${s.id}-heading`}
                className="mt-12 scroll-mt-28 border-lp-green-mid/40 border-t pt-10 md:mt-14"
              >
                <h2
                  id={`${s.id}-heading`}
                  className="m-0 mb-5 font-display font-semibold text-2xl text-lp-green-dk md:text-3xl"
                >
                  {s.title}
                </h2>
                <div className="flex flex-col gap-4">{s.body}</div>
              </section>
            ))}
          </article>
        </div>
      </div>
    </div>
  );
}
